import { useEffect, useRef, useState } from "react";
import { Tooltip } from "react-tooltip";
import { UserType } from "types/UsersTypes";
import {
  useGetUserAssetsAdmin,
  useGetUserSuccessorAccountsAdmin,
  useGetAdminInvestmentDetails,
} from "hooks/investments";
import {
  UserAssetCategoryGroup,
  UserAssetRow,
} from "hooks/investments/GetUserAssetsAdminHook";
import {
  AssetCategorySlug,
  ASSET_CATEGORY_NAMES,
  ASSET_CATEGORY_SLUG,
  getAssetCategorySvg,
} from "enums/AssetCategoryEnum";
import { SVG_ENUM } from "enums";
import SVG from "shared/SVG";
import AssetDetailsSideModal from "screens/investmentsPage/components/AssetDetailsSideModal/AssetDetailsSideModal";
import InvestmentTransactionsSideModal from "screens/investmentsPage/components/InvestmentTransactionsSideModal/InvestmentTransactionsSideModal";
import ShareTransactionsSideModal from "./ShareTransactionsSideModal";
import SuccessorOwnerPicker from "./SuccessorOwnerPicker";
import "./UserAssetsList.scss";

const TOOLTIP_ID = "user-assets-list-info-tooltip";

type ViewMode = "own" | "successor";

interface UserAssetsListProps {
  user: UserType | null;
}

interface CategoryAccordionProps {
  group: UserAssetCategoryGroup;
  origin: "reliance" | "other";
  onOpenItem: (item: UserAssetRow) => void;
}

const CategoryAccordion = ({
  group,
  origin,
  onOpenItem,
}: CategoryAccordionProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isCurrencyCategory =
    group.categorySlug === ASSET_CATEGORY_SLUG.CURRENCIES;
  const isCryptoCategory = group.categorySlug === ASSET_CATEGORY_SLUG.CRYPTO;
  // W Aktywach w Reliance kolumnę Status pokazujemy tylko dla walut i
  // krypto (tam status pochodzi z realnego stanu ustawianego przez admina
  // na puli); dla pozostałych kategorii (obligacje, pożyczki itd.) nie ma
  // kolumny Status. Aktywa poza Reliance mają Dostęp (klient sam decyduje,
  // czy Reliance je widzi).
  const showStatusColumn =
    origin === "reliance" && (isCurrencyCategory || isCryptoCategory);
  const showAccessColumn = origin === "other";

  const contentWrapperRef = useRef<HTMLDivElement>(null);
  const contentInnerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const wrapper = contentWrapperRef.current;
    const inner = contentInnerRef.current;
    if (!wrapper || !inner) return;

    if (isExpanded) {
      const onTransitionEnd = (e: TransitionEvent) => {
        if (e.propertyName === "grid-template-rows") {
          inner.style.overflow = "visible";
        }
      };
      wrapper.addEventListener("transitionend", onTransitionEnd, {
        once: true,
      });
      return () => {
        wrapper.removeEventListener("transitionend", onTransitionEnd);
      };
    } else {
      inner.style.overflow = "hidden";
    }
  }, [isExpanded]);

  return (
    <div className="user-assets-list__category">
      <div
        className="user-assets-list__category-header"
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        <span className="user-assets-list__category-icon">
          <SVG type={getAssetCategorySvg(group.categorySlug)} />
        </span>
        <span className="user-assets-list__category-title">
          {ASSET_CATEGORY_NAMES[group.categorySlug as AssetCategorySlug] ??
            group.categorySlug}
        </span>
        <span className="user-assets-list__category-count">
          ({group.count})
        </span>
        <div
          className={`user-assets-list__chevron ${
            isExpanded ? "user-assets-list__chevron--expanded" : ""
          }`}
        >
          <SVG type={SVG_ENUM.CHEVRON_DOWN} />
        </div>
      </div>
      <div
        ref={contentWrapperRef}
        className={`user-assets-list__category-content-wrapper ${
          isExpanded ? "user-assets-list__category-content-wrapper--expanded" : ""
        }`}
      >
        <div
          ref={contentInnerRef}
          className="user-assets-list__category-content-inner"
        >
        <div className="user-assets-list__category-content">
          <div className="user-assets-list__row user-assets-list__row--head">
            <span>Nazwa</span>
            <span
              className={`user-assets-list__row-meta ${
                isCurrencyCategory ? "user-assets-list__row-meta--single" : ""
              }`}
            >
              {!isCurrencyCategory && <span>Waluta</span>}
              {showStatusColumn && <span>Status</span>}
              {showAccessColumn && <span>Dostęp</span>}
            </span>
          </div>
          {group.items.map((item: UserAssetRow) => {
            const isTransactionsItem =
              (item.source === "pool_share" &&
                (isCurrencyCategory || isCryptoCategory)) ||
              item.source === "share";
            const isClickable =
              (item.source === "investment" || isTransactionsItem) &&
              (origin === "reliance" || item.accessLabel === "Tak");
            return (
            <div
              className={`user-assets-list__row ${
                isClickable ? "user-assets-list__row--clickable" : ""
              }`}
              key={item.uuid}
              onClick={isClickable ? () => onOpenItem(item) : undefined}
            >
              <span className="user-assets-list__row-name">
                {item.infoNote && (
                  <span
                    className="user-assets-list__info-icon"
                    data-tooltip-id={TOOLTIP_ID}
                    data-tooltip-content={item.infoNote}
                  >
                    <SVG type={SVG_ENUM.INFO} />
                  </span>
                )}
                {isCurrencyCategory
                  ? item.currencyCode ?? "-"
                  : item.name ?? "-"}
              </span>
              <span
                className={`user-assets-list__row-meta ${
                  isCurrencyCategory ? "user-assets-list__row-meta--single" : ""
                }`}
              >
                {!isCurrencyCategory && (
                  <span>{item.currencyCode ?? "-"}</span>
                )}
                {showStatusColumn && (
                  <span
                    className={`user-assets-list__status-badge ${
                      item.statusKey
                        ? `user-assets-list__status-badge--${item.statusKey}`
                        : ""
                    }`}
                  >
                    {item.statusLabel ?? "-"}
                  </span>
                )}
                {showAccessColumn && (
                  <span
                    className={`user-assets-list__access ${
                      item.accessLabel === "Tak"
                        ? "user-assets-list__access--yes"
                        : "user-assets-list__access--no"
                    }`}
                  >
                    <SVG
                      type={
                        item.accessLabel === "Tak"
                          ? SVG_ENUM.LOCK_OPEN
                          : SVG_ENUM.LOCK
                      }
                    />
                    {item.accessLabel ?? "-"}
                  </span>
                )}
              </span>
            </div>
            );
          })}
        </div>
        </div>
      </div>
    </div>
  );
};

const UserAssetsList: React.FC<UserAssetsListProps> = ({ user }) => {
  const [viewMode, setViewMode] = useState<ViewMode>("own");
  const [selectedOwnerUuid, setSelectedOwnerUuid] = useState<string | null>(
    null
  );

  useEffect(() => {
    setViewMode("own");
    setSelectedOwnerUuid(null);
  }, [user?.uuid]);

  const { data: successorAccounts = [] } = useGetUserSuccessorAccountsAdmin({
    userUuid: user?.uuid,
  });

  useEffect(() => {
    if (
      viewMode === "successor" &&
      !selectedOwnerUuid &&
      successorAccounts.length > 0
    ) {
      setSelectedOwnerUuid(successorAccounts[0].ownerUuid);
    }
  }, [viewMode, selectedOwnerUuid, successorAccounts]);

  const isSuccessorView = viewMode === "successor";

  const { data, isLoading } = useGetUserAssetsAdmin({
    userUuid: user?.uuid,
    asSuccessorOfUuid: isSuccessorView ? selectedOwnerUuid : undefined,
    enabled: !isSuccessorView || !!selectedOwnerUuid,
  });

  const relianceGroups = data?.reliance ?? [];
  const otherGroups = data?.other ?? [];
  const hasAnyAssets = relianceGroups.length > 0 || otherGroups.length > 0;

  const [detailsItem, setDetailsItem] = useState<UserAssetRow | null>(null);
  const { data: investmentDetails } = useGetAdminInvestmentDetails({
    uuid: detailsItem?.uuid,
    enabled: !!detailsItem,
  });

  const [transactionsItem, setTransactionsItem] =
    useState<UserAssetRow | null>(null);
  const [shareTransactionsItem, setShareTransactionsItem] =
    useState<UserAssetRow | null>(null);

  const handleOpenItem = (item: UserAssetRow) => {
    if (item.source === "pool_share") {
      setTransactionsItem(item);
    } else if (item.source === "share") {
      setShareTransactionsItem(item);
    } else {
      setDetailsItem(item);
    }
  };

  return (
    <div className="user-assets-list">
      <Tooltip id={TOOLTIP_ID} place="top" className="tooltip" />
      {successorAccounts.length > 0 && (
        <div className="user-assets-list__segment">
          <button
            className={`user-assets-list__segment-item ${
              !isSuccessorView ? "user-assets-list__segment-item--active" : ""
            }`}
            onClick={() => setViewMode("own")}
          >
            Własne aktywa
          </button>
          <button
            className={`user-assets-list__segment-item ${
              isSuccessorView ? "user-assets-list__segment-item--active" : ""
            }`}
            onClick={() => setViewMode("successor")}
          >
            Jako sukcesor ({successorAccounts.length})
          </button>
        </div>
      )}
      {isSuccessorView && (
        <>
          <h2 className="user-assets-list__section-title--sukcesja">
            Aktywa w sukcesji
          </h2>
          <SuccessorOwnerPicker
            accounts={successorAccounts}
            selectedOwnerUuid={selectedOwnerUuid}
            onSelect={setSelectedOwnerUuid}
          />
        </>
      )}
      {!isLoading && !hasAnyAssets && (
        <h3 className="side-modal__table-heading">Brak danych</h3>
      )}
      {relianceGroups.length > 0 && (
        <>
          <h2>Aktywa w Reliance</h2>
          {relianceGroups.map((group) => (
            <CategoryAccordion
              key={group.categorySlug}
              group={group}
              origin="reliance"
              onOpenItem={handleOpenItem}
            />
          ))}
        </>
      )}
      {otherGroups.length > 0 && (
        <>
          <h2 className="user-assets-list__section-title--other">
            Aktywa poza Reliance
          </h2>
          {otherGroups.map((group) => (
            <CategoryAccordion
              key={group.categorySlug}
              group={group}
              origin="other"
              onOpenItem={handleOpenItem}
            />
          ))}
        </>
      )}
      <AssetDetailsSideModal
        isOpen={!!detailsItem}
        onClose={() => setDetailsItem(null)}
        item={investmentDetails ?? null}
        slug={detailsItem?.categorySlug ?? ""}
      />
      <InvestmentTransactionsSideModal
        isOpen={!!transactionsItem}
        onClose={() => setTransactionsItem(null)}
        item={
          transactionsItem
            ? {
                uuid: transactionsItem.investmentUuid,
                investmentShareUuid: transactionsItem.investmentShareUuid,
                currency: { currency: transactionsItem.currencyCode },
                owner: user ? { name: user.name } : undefined,
              }
            : null
        }
        investmentType={{ slug: transactionsItem?.categorySlug ?? "" }}
      />
      <ShareTransactionsSideModal
        isOpen={!!shareTransactionsItem}
        onClose={() => setShareTransactionsItem(null)}
        item={shareTransactionsItem}
        ownerName={user?.name}
      />
    </div>
  );
};

export default UserAssetsList;
